import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * Řidiči-referenti - V1 DRAFT - authored for structural completeness; NOT a
 * substitute for sign-off by a Czech osoba odborně způsobilá v prevenci rizik.
 * Legal refs: zákoník práce 262/2006 Sb. §103 (obecná školicí povinnost - na
 * "referentské" řízení se NEvztahuje profesní školení dle zák. 247/2000 Sb.);
 * zákon 361/2000 Sb. o provozu na pozemních komunikacích. E-learning delivery
 * for this category was upheld in court practice. Bilingual (cs-CZ + en-US).
 * Three chapters (právní rámec | před jízdou | za jízdy) + a graded quiz with
 * one question per kind. Answer keys live here (repo-privileged) and in the
 * DB; stripped from the client payload by the API.
 */
export const driversReferentsProfile: TrainingProfile = {
  slug: 'drivers-referents',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column (V1 Czech). Chapter titles are LocalizedText.
  title: 'Řidiči-referenti',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-ramec',
        title: t(
          'Kdo je řidič-referent a proč se školí',
          'Who is a "reference driver" and why train',
        ),
        minDwellSeconds: 20,
        blocks: [
          {
            id: 'h-ramec',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Jízda k zakázce je taky práce', 'Driving to a job is work too'),
          },
          {
            id: 'p-ramec-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Řidič-referent** je každý, kdo řídí vozidlo při výkonu práce, i když „řidič" není jeho profese - typicky cesta dodávkou k zákazníkovi. Dopravní nehody patří mezi **nejčastější příčiny smrtelných pracovních úrazů** v ČR.',
              'A **"reference driver"** is anyone who drives while working, even if driving is not their profession - typically the van ride to a customer. Traffic accidents are among the **most common causes of fatal occupational injuries** in Czechia.',
            ),
          },
          {
            id: 'c-ramec-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **zákoník práce §103** (seznámení s riziky práce) a **zákon 361/2000 Sb.** (pravidla provozu). Profesní průkaz dle zák. 247/2000 Sb. se na referentské řízení **nevztahuje**.',
              'Legal basis: **Labour Code §103** (acquaintance with work risks) and **Act 361/2000 Coll.** (road traffic rules). The professional driver licence under Act 247/2000 Coll. does **not** apply to reference driving.',
            ),
          },
          {
            id: 'k-ramec',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Řídíš-li k zakázce, platí pro tebe tento kurz - bez ohledu na to, že tvou profesí je instalatérství nebo elektro.',
              'If you drive to jobs, this course applies to you - regardless of your trade being plumbing or electrics.',
            ),
          },
        ],
      },
      {
        id: 'ch-pred-jizdou',
        title: t('Před jízdou: vozidlo, náklad, řidič', 'Before the trip: vehicle, cargo, driver'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-naklad'],
        blocks: [
          {
            id: 'h-pred',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Kontrola před vyjetím', 'Pre-drive check'),
          },
          {
            id: 'cl-pred',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Stav vozidla: pneumatiky, světla, kapaliny, doklady',
                'Vehicle condition: tyres, lights, fluids, documents',
              ),
              t(
                'Náklad a nářadí upevněné proti pohybu',
                'Cargo and tools secured against movement',
              ),
              t('Trasa a čas naplánované bez spěchu', 'Route and time planned without rushing'),
              t(
                'Jsem odpočatý, bez alkoholu a léků ovlivňujících pozornost',
                'I am rested, no alcohol or attention-impairing medication',
              ),
            ],
          },
          {
            id: 'p-pred-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Nezajištěné nářadí je při nárazu **projektil**: kufr s nářadím o hmotnosti 20 kg má při nárazu v 50 km/h účinek přes půl tuny. Nářadí patří do úchytů, těžké věci dolů a k přepážce.',
              'Unsecured tools become **projectiles** in a crash: a 20 kg toolbox hits with over half a tonne of force at 50 km/h. Tools belong in restraints, heavy items low and against the bulkhead.',
            ),
          },
          {
            id: 'anim-naklad',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'cargoShift',
            steps: [
              {
                label: t('Volný náklad', 'Loose cargo'),
                say: t(
                  'Při brzdění letí kufr do kabiny.',
                  'Under braking the box flies into the cab.',
                ),
              },
              {
                label: t('Upevněný náklad', 'Strapped cargo'),
                say: t('Popruhy nebo síť - náklad drží.', 'Straps or a net - the load stays put.'),
              },
            ],
            caption: t(
              'Při brzdění letí neupevněný náklad dopředu - popruh ho udrží na místě.',
              'Under braking, unsecured cargo flies forward - a strap keeps it in place.',
            ),
          },
          {
            id: 'kc-naklad',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Kam ve skříňové dodávce patří nejtěžší náklad?',
              'Where does the heaviest cargo belong in a panel van?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Dolů, co nejblíž přepážce za kabinou',
                  'Low, as close to the cab bulkhead as possible',
                ),
              },
              {
                id: 'kc-b',
                label: t('Nahoru na regál, ať je po ruce', 'Up on the shelf, within easy reach'),
              },
              {
                id: 'kc-c',
                label: t('K zadním dveřím kvůli vykládce', 'By the rear doors for easy unloading'),
              },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Nízko uložený náklad u přepážky snižuje těžiště a při brzdění se neposune do kabiny.',
              'Cargo stored low against the bulkhead lowers the centre of gravity and cannot shift into the cab under braking.',
            ),
          },
        ],
      },
      {
        id: 'ch-za-jizdy',
        title: t('Za jízdy: telefon, únava, podmínky', 'On the road: phone, fatigue, conditions'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-unava'],
        blocks: [
          {
            id: 'h-za',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Největší rizika jsou v kabině', 'The biggest risks sit in the cab'),
          },
          {
            id: 'c-telefon-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              'Psaní zprávy za volantem znamená jet **několik sekund poslepu**. Vyřizování zakázek za jízdy počká - telefon patří do držáku a hovory přes handsfree, ideálně až po zastavení.',
              'Typing a message at the wheel means driving **blind for several seconds**. Job messages can wait - the phone belongs in a cradle, calls via hands-free, ideally after you stop.',
            ),
          },
          {
            id: 'p-za-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              '**Mikrospánek** nepřichází náhle - ohlašuje se těžkými víčky, zíváním a „děravými" úseky cesty. Jediná účinná obrana je zastavit a **15-20 minut spát**; kofein a otevřené okno únavu jen maskují.',
              '**Microsleep** does not strike from nowhere - heavy eyelids, yawning and "missing" stretches of road announce it. The only effective defence is stopping for a **15-20 minute nap**; caffeine and an open window merely mask fatigue.',
            ),
          },
          {
            id: 'ord-unava',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď správnou reakci na příznaky únavy za volantem.',
              'Order the correct response to fatigue at the wheel.',
            ),
            items: [
              {
                id: 'ou-1',
                label: t(
                  'Rozpoznám příznaky (těžká víčka, zívání)',
                  'Recognise the signs (heavy eyelids, yawning)',
                ),
              },
              {
                id: 'ou-2',
                label: t(
                  'Najdu nejbližší bezpečné místo k zastavení',
                  'Find the nearest safe place to stop',
                ),
              },
              { id: 'ou-3', label: t('Krátký spánek 15-20 minut', 'Short 15-20 minute nap') },
              {
                id: 'ou-4',
                label: t(
                  'Pokračuji, jen pokud únava odezněla',
                  'Continue only once the fatigue has passed',
                ),
              },
            ],
          },
          {
            id: 'k-za',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Spěch na zakázku nikdy nevyváží nehodu. **Telefon v držáku, přestávka při únavě, rychlost podle podmínek.**',
              'Rushing to a job never outweighs a crash. **Phone in the cradle, break when tired, speed matched to conditions.**',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-unava-single',
      kind: 'single',
      chapterId: 'ch-za-jizdy',
      prompt: t(
        'Co je jediná účinná obrana proti mikrospánku?',
        'What is the only effective defence against microsleep?',
      ),
      choices: [
        {
          id: 'a',
          label: t('Silná káva a energetický nápoj', 'Strong coffee and an energy drink'),
        },
        { id: 'b', label: t('Zastavit a 15-20 minut spát', 'Stop and nap for 15-20 minutes') },
        {
          id: 'c',
          label: t('Otevřít okno a zesílit rádio', 'Open the window and turn up the radio'),
        },
      ],
      correct: 'b',
    },
    {
      id: 'q-telefon-tf',
      kind: 'truefalse',
      chapterId: 'ch-za-jizdy',
      prompt: t(
        'Krátkou zprávu zákazníkovi je v pořádku napsat za jízdy, pokud jedu pomalu.',
        'A short message to a customer is fine to type while driving, as long as I drive slowly.',
      ),
      correct: false,
    },
    {
      id: 'q-pred-multi',
      kind: 'multi',
      chapterId: 'ch-pred-jizdou',
      prompt: t(
        'Co patří do kontroly před vyjetím? (vyber všechny správné)',
        'What belongs in the pre-drive check? (select all that apply)',
      ),
      choices: [
        { id: 'a', label: t('Upevnění nákladu a nářadí', 'Securing cargo and tools') },
        { id: 'b', label: t('Stav pneumatik a světel', 'Tyre and light condition') },
        { id: 'c', label: t('Naleštění karoserie', 'Polishing the bodywork') },
        { id: 'd', label: t('Vlastní odpočinek a střízlivost', 'My own rest and sobriety') },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-spanek-numeric',
      kind: 'numeric',
      chapterId: 'ch-za-jizdy',
      prompt: t(
        'Kolik minut má trvat krátký spánek proti únavě (spodní hranice)?',
        'How many minutes should the anti-fatigue nap last (lower bound)?',
      ),
      correct: 15,
      tolerance: 0,
    },
    {
      id: 'q-unava-order',
      kind: 'ordering',
      chapterId: 'ch-za-jizdy',
      prompt: t(
        'Seřaď reakci na únavu za volantem.',
        'Order the response to fatigue at the wheel.',
      ),
      choices: [
        { id: 'a', label: t('Rozpoznání příznaků', 'Recognise the signs') },
        { id: 'b', label: t('Bezpečné zastavení', 'Stop safely') },
        { id: 'c', label: t('Krátký spánek', 'Short nap') },
        { id: 'd', label: t('Pokračování po odeznění únavy', 'Continue once fatigue passes') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
